import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe, ArrowLeft } from "lucide-react";
import { REGIONS, slugify } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { SectionLabel, PlaceCard } from "../components/Bits";

export default function Destinations() {
  const { places, geo, loading } = usePlaces();
  const countries = geo.countries;
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState(null);

  const activeCountry = country || countries[0];
  const regionNames = (geo.regionsByCountry[activeCountry] || []);
  const regionCards = REGIONS.filter((r) => r.country === activeCountry && regionNames.includes(r.name));
  const regionPlaces = region ? places.filter((p) => p.country === activeCountry && p.region === region) : [];
  const cities = region ? (geo.citiesByRegion[`${activeCountry}|${region}`] || []) : [];

  const countPlaces = (c) => places.filter((p) => p.country === c).length;
  const countRegion = (r) => places.filter((p) => p.country === activeCountry && p.region === r).length;

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  return (
    <div className="pt-[68px]">
      <div className="bg-ink text-paper relative grain overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-brass" />
            <span className="eyebrow text-brass">Destinations</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl font-semibold leading-[1.0] display-balance max-w-3xl">
            Explore the world, one place at a time.
          </h1>
          <p className="text-paper/60 mt-6 text-lg max-w-xl leading-relaxed">
            Placeworthy is building an independent guide across the globe. Choose a country, drill into a
            province or state, then a city — and find places genuinely worth experiencing.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-10">
            {countries.map((c) => (
              <button key={c} onClick={() => { setCountry(c); setRegion(null); }} className={`px-6 py-3 text-sm font-semibold tracking-wide transition-colors ${activeCountry === c ? "bg-brass text-ink" : "border border-paper/25 text-paper hover:bg-paper/10"}`}>
                {c} <span className="opacity-60">· {countPlaces(c)}</span>
              </button>
            ))}
            <span className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-paper/40 border border-paper/15 cursor-not-allowed">
              <Globe className="w-4 h-4" /> More coming
            </span>
          </div>
        </div>
      </div>

      {!region ? (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <SectionLabel>{activeCountry} · provinces & states</SectionLabel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
            {regionCards.map((r, i) => (
              <button key={r.name} onClick={() => setRegion(r.name)} className="group text-left rise-in" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="img-zoom relative aspect-[3/2] bg-ink/5">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <h3 className="font-serif text-paper text-3xl font-semibold leading-none">{r.name}</h3>
                    <span className="text-paper eyebrow text-[10px]">{countRegion(r.name)} places</span>
                  </div>
                </div>
                <p className="text-sm text-ink-soft mt-3 leading-relaxed">{r.tagline}</p>
                <span className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-claret group-hover:gap-2.5 transition-all">
                  Explore {r.name} <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <button onClick={() => setRegion(null)} className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm mb-8">
            <ArrowLeft className="w-4 h-4" /> All of {activeCountry}
          </button>
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
            <div>
              <SectionLabel>{activeCountry} · {region}</SectionLabel>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-semibold mt-4">{region}</h2>
              <p className="text-ink-soft text-lg mt-4 leading-relaxed max-w-lg">{REGIONS.find((r) => r.name === region)?.tagline}</p>
              <div className="mt-8">
                <span className="eyebrow text-ink-soft text-[10px]">Cities we're in</span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {cities.length ? cities.map((c) => (
                    <Link key={c} to={`/city/${slugify(c)}`} className="px-4 py-2 bg-ink/5 hover:bg-claret hover:text-paper text-sm font-medium transition-colors">{c}</Link>
                  )) : <span className="text-ink-soft text-sm">Coming soon.</span>}
                </div>
              </div>
              <Link to={`/explore?country=${encodeURIComponent(activeCountry)}&region=${encodeURIComponent(region)}`} className="inline-flex items-center gap-2 mt-8 px-6 py-3.5 bg-ink text-paper text-sm font-semibold hover:bg-claret transition-colors">
                View all on the map <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="border border-ink/12 overflow-hidden bg-paper">
              <iframe title={`${region} map`} src={`https://maps.google.com/maps?q=${encodeURIComponent(`${region}, ${activeCountry}`)}&z=6&output=embed`} className="w-full h-[360px] grayscale-[0.15]" loading="lazy" style={{ border: 0 }} />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="font-serif text-3xl font-semibold mb-8">Reviewed in {region}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
              {regionPlaces.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
